import { useEffect, useState, useCallback } from "react";
import { Receipt, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const label = (s) => (s || "").replace(/_/g, " ");

export function AdminRefunds() {
  const [data, setData] = useState({ refunds: [], pending_total: 0, count: 0 });
  const load = useCallback(() => { api.get("/marketplace/admin/refunds").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const process = async (id, mode) => {
    const msg = mode === "razorpay" ? "Process a REAL Razorpay refund to the customer now?" : "Record this refund (offline settlement)? Inventory will be restored.";
    if (!window.confirm(msg)) return;
    try { const r = await api.post(`/marketplace/admin/refunds/${id}`, { mode }); toast.success(mode === "razorpay" ? "Razorpay refund initiated" : `Refund recorded: ₹${r.data.amount}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-refunds-page">
      <PageHeader title="Refunds" icon={Receipt} subtitle="Orders flagged for refund (cancellations & approved returns). Refunds are recorded here; money is settled to the customer's original method." />
      <div className="mb-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-2xl font-bold text-slate-900" data-testid="refund-pending-total">₹{data.pending_total}</p><p className="text-xs text-slate-500">Pending refund amount</p></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-2xl font-bold text-slate-900">{data.count}</p><p className="text-xs text-slate-500">Pending refunds</p></div>
      </div>
      {data.refunds.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="refunds-empty">
          <Receipt className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-3 font-semibold text-slate-700">No pending refunds</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.refunds.map((s) => (
            <div key={s.suborder_id} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4" data-testid={`refund-${s.suborder_id}`}>
              <div>
                <p className="text-sm font-bold text-slate-800">{s.number} <span className="font-normal text-slate-400">· {s.master_number}</span></p>
                <p className="text-xs text-slate-500">{s.address?.name} · status {label(s.status)} · {s.items?.map((it) => `${it.name}×${it.qty}`).join(", ")}</p>
                {s.cancellation?.reason && <p className="text-xs text-rose-500">Reason: {s.cancellation.reason}</p>}
                {s.return?.reason && <p className="text-xs text-rose-500">Return: {s.return.reason}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-slate-900">₹{s.subtotal}</span>
                <button onClick={() => process(s.suborder_id, "record")} data-testid={`record-refund-${s.suborder_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600"><RotateCcw className="h-3.5 w-3.5" /> Record (offline)</button>
                <button onClick={() => process(s.suborder_id, "razorpay")} data-testid={`razorpay-refund-${s.suborder_id}`} className="rounded-full brand-gradient-bg px-3 py-2 text-xs font-semibold text-white">Refund via Razorpay</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
